import { ChevronsUpDown } from "lucide-react";
import { FC, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "../avatar";
import { Button } from "../button";
import { useTenantClientSelector } from "./useTenantClientSelector";

import clientLogo from "@/assets/client-logo.png";
import { ClientProvider } from "@/lib/contexts/Client/ClientProvider";
import { useAuth } from "@/lib/hooks/useAuth";
import { useClients } from "@/lib/hooks/useClients";
import { cn } from "@/lib/utils/cn";

type ClientLoadedIndicatorContentProps = { clientId?: string };

const ClientLoadedIndicatorContent: FC<ClientLoadedIndicatorContentProps> = ({
  clientId,
}) => {
  const { client } = useClients();
  const location = useLocation();
  const { open, setOpen, setOpenedFromClientTrigger } =
    useTenantClientSelector();
  const wasOpenOnPointerDownRef = useRef(false);

  const clientName =
    client?.name ??
    (location.state as { clientName?: string } | null)?.clientName ??
    "";

  return (
    <Button
      type="button"
      variant="ghost"
      className={cn(
        "flex gap-2 items-center hover:opacity-80 transition-opacity text-sm h-auto py-1.5 px-1",
        "duration-300 transition-all w-full",
      )}
      onPointerDown={() => {
        wasOpenOnPointerDownRef.current = open;
      }}
      onClick={() => {
        const wasOpen = wasOpenOnPointerDownRef.current;
        if (wasOpen) {
          setOpen(false);
        } else {
          setOpenedFromClientTrigger(true);
          setOpen(true);
        }
      }}
      data-state={clientId ? "show" : "hide"}
    >
      <Avatar className="size-6 shrink-0">
        <AvatarImage src={client?.imagePath || clientLogo} />
        <AvatarFallback>
          {clientName ? clientName.charAt(0) : "C"}
        </AvatarFallback>
      </Avatar>
      <span className="truncate transition-opacity min-w-0">{clientName}</span>
      <ChevronsUpDown className="size-3 shrink-0 opacity-50" />
    </Button>
  );
};

const ClientLoadedIndicator: FC = () => {
  const { clientId } = useParams();
  const { tenantId } = useAuth();

  return (
    <ClientProvider clientId={clientId} tenantId={tenantId}>
      <div className="flex items-center gap-3 h-full">
        <ClientLoadedIndicatorContent clientId={clientId} />
      </div>
    </ClientProvider>
  );
};
export default ClientLoadedIndicator;
